import React, { useState, useEffect } from 'react';
import {
  PieChart, Pie, Cell, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  PieChart as PieChartIcon, BarChart3, TrendingUp, Calendar,
  ArrowLeft, Download, RefreshCw, DollarSign, FileText,
  CheckCircle, XCircle, Building2, Clock, Users, Database,
  Activity, Target, Shield, Zap, Mail, MousePointer, Eye,
  FlaskConical, Trophy, Percent, Lock, Unlock, RotateCcw,
  AlertTriangle, CheckCircle2, XOctagon, Pause, Play
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/TestAuthContext';
import { api, apiFetch } from '../utils/api';
import { formatCurrency } from '../utils/financialFormat';
import { FinancialValue } from '../components/shared/FinancialValue';
import ExtractionAnalytics from '../components/analytics/ExtractionAnalytics';
import {
  CHART_PALETTE,
  CHART_DONE_COLOR,
  CHART_NEGATIVE_COLOR,
  CHART_AXIS_STYLE,
  CHART_GRID_STYLE,
  CHART_TOOLTIP_STYLE,
  CHART_TOOLTIP_LABEL_STYLE,
  CHART_LEGEND_STYLE,
  getSeriesColor,
  getStatusColor,
} from '../config/chartTheme';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Updated color palettes using brand theme
const STATUS_COLORS: Record<string, string> = {
  completed: CHART_DONE_COLOR,
  pending: CHART_PALETTE[1],  // Gray 400
  draft: CHART_PALETTE[2],    // Gray 500
  failed: CHART_NEGATIVE_COLOR, // Muted gray (never red)
  unknown: CHART_PALETTE[3],  // Gray 600
};

// Event colors using brand palette (orange + grayscale)
const EVENT_COLORS = [
  CHART_PALETTE[0], // Brand orange
  CHART_PALETTE[1], // Gray 400
  CHART_PALETTE[2], // Gray 500
  CHART_PALETTE[3], // Gray 600
  CHART_PALETTE[4], // Gray 300
  CHART_PALETTE[5], // Gray 700
];

// Chart colors using brand palette
const CHART_COLORS = [
  CHART_PALETTE[0], // Brand orange
  CHART_DONE_COLOR, // Green for done
  CHART_PALETTE[1], // Gray 400
  CHART_NEGATIVE_COLOR, // Muted gray
  CHART_PALETTE[2], // Gray 500
  CHART_PALETTE[3], // Gray 600
];

// Health score colors - only green for ready, gray tones for others
const HEALTH_COLORS = {
  green: CHART_DONE_COLOR,
  yellow: CHART_PALETTE[1], // Gray 400 instead of amber
  red: CHART_PALETTE[3],    // Gray 600 instead of red
};

// Calculation mode colors using brand palette
const MODE_COLORS = {
  historical: CHART_PALETTE[0], // Brand orange
  future_closure: CHART_PALETTE[1], // Gray 400
};

// Data source colors using brand palette
const SOURCE_COLORS = {
  actual: CHART_DONE_COLOR,
  bls_average: CHART_PALETTE[1], // Gray 400 instead of amber
  mixed: CHART_PALETTE[0], // Brand orange
};

interface AnalyticsData {
  summary: {
    total_claims: number;
    total_value: number;
    average_value: number;
    completed_count: number;
    pending_count: number;
    recent_claims_7d: number;
    avg_completion_days?: number;
  };
  status_breakdown: Array<{ status: string; count: number }>;
  event_type_breakdown: Array<{ event_type: string; count: number; total_value: number }>;
  business_type_breakdown: Array<{ business_type: string; count: number }>;
  causation_results: {
    passed: number;
    failed: number;
    success_rate: number;
  };
  monthly_trends: Array<{ month: string; claims: number; value: number }>;
  top_claims: Array<{
    id: string;
    client_name: string;
    event_type: string;
    value: number;
    status: string;
  }>;
  // NEW ANALYTICS
  health_score_distribution?: { green: number; yellow: number; red: number };
  calculation_mode_split?: { historical: number; future_closure: number };
  data_source_usage?: { actual: number; bls_average: number; mixed: number };
  top_clients?: Array<{ client_name: string; count: number }>;
  claim_value_distribution?: Array<{ bucket: string; count: number }>;
  monte_carlo_aggregate?: {
    claims_with_mc: number;
    avg_ci_width_pct: number;
    avg_p50: number;
    total_ci_low: number;
    total_ci_high: number;
  };
}

export default function AnalyticsPage() {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);

  // Vendor Classification History
  const [vendorHistory, setVendorHistory] = useState<any>(null);
  const [vendorHistoryLoading, setVendorHistoryLoading] = useState(false);

  // Email Engagement Analytics
  const [emailAnalytics, setEmailAnalytics] = useState<any>(null);
  const [emailAnalyticsLoading, setEmailAnalyticsLoading] = useState(false);

  // A/B Test Results
  const [abTestResults, setAbTestResults] = useState<any>(null);
  const [abTestLoading, setAbTestLoading] = useState(false);
  const [lockedWinners, setLockedWinners] = useState<any>({});
  const [applyingWinner, setApplyingWinner] = useState<string | null>(null);

  // Email Retry Queue
  const [retryStats, setRetryStats] = useState<any>(null);
  const [retryHistory, setRetryHistory] = useState<any[]>([]);
  const [retryLoading, setRetryLoading] = useState(false);
  const [processingRetries, setProcessingRetries] = useState(false);
  const [cancellingRetry, setCancellingRetry] = useState<string | null>(null);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = await getToken();

      const result = await api.get('/api/v1/analytics/summary', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(result);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorHistory = async () => {
    try {
      setVendorHistoryLoading(true);
      const token = await getToken();

      const result = await api.get('/api/v1/vendor-intelligence/classification-history?months=6', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVendorHistory(result);
    } catch (err: any) {
      console.error('Failed to fetch vendor history:', err);
    } finally {
      setVendorHistoryLoading(false);
    }
  };

  const fetchEmailAnalytics = async () => {
    try {
      setEmailAnalyticsLoading(true);
      const token = await getToken();

      // Fetch aggregated email analytics across all reports
      const result = await api.get('/api/v1/analytics/email-engagement', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailAnalytics(result);
    } catch (err: any) {
      console.error('Failed to fetch email analytics:', err);
    } finally {
      setEmailAnalyticsLoading(false);
    }
  };

  const fetchAbTestResults = async () => {
    try {
      setAbTestLoading(true);
      const token = await getToken();

      // Fetch both results and locked winners in parallel
      const [resultsRes, winnersRes] = await Promise.all([
        api.get('/api/v1/analytics/ab-test-results?email_type=evidence_request', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/api/v1/analytics/ab-test/locked-winners?email_type=evidence_request', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setAbTestResults(resultsRes);
      setLockedWinners(winnersRes?.locked_winners || {});
    } catch (err: any) {
      console.error('Failed to fetch A/B test results:', err);
    } finally {
      setAbTestLoading(false);
    }
  };

  const applyWinner = async (testKey: string, variantId: string) => {
    try {
      setApplyingWinner(`${testKey}_${variantId}`);
      const token = await getToken();

      await api.post('/api/v1/analytics/ab-test/apply-winner', {
        email_type: 'evidence_request',
        test_key: testKey,
        variant_id: variantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh data
      await fetchAbTestResults();
    } catch (err: any) {
      console.error('Failed to apply winner:', err);
    } finally {
      setApplyingWinner(null);
    }
  };

  const clearWinner = async (testKey: string) => {
    try {
      setApplyingWinner(`clear_${testKey}`);
      const token = await getToken();

      await api.post('/api/v1/analytics/ab-test/clear-winner', {
        email_type: 'evidence_request',
        test_key: testKey
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh data
      await fetchAbTestResults();
    } catch (err: any) {
      console.error('Failed to clear winner:', err);
    } finally {
      setApplyingWinner(null);
    }
  };

  const fetchRetryStats = async () => {
    try {
      setRetryLoading(true);
      const token = await getToken();

      // Fetch both stats and history in parallel
      const [statsRes, historyRes] = await Promise.all([
        api.get('/api/v1/analytics/email-retry/stats', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        api.get('/api/v1/analytics/email-retry/history?limit=10', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      setRetryStats(statsRes?.retry_stats || null);
      setRetryHistory(historyRes?.jobs || []);
    } catch (err: any) {
      console.error('Failed to fetch retry stats:', err);
    } finally {
      setRetryLoading(false);
    }
  };

  const processRetries = async () => {
    try {
      setProcessingRetries(true);
      const token = await getToken();

      await api.post('/api/v1/analytics/email-retry/process-pending', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh data after processing
      await fetchRetryStats();
    } catch (err: any) {
      console.error('Failed to process retries:', err);
    } finally {
      setProcessingRetries(false);
    }
  };

  const cancelRetry = async (jobId: string) => {
    try {
      setCancellingRetry(jobId);
      const token = await getToken();

      await api.post(`/api/v1/analytics/email-retry/${jobId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Refresh data
      await fetchRetryStats();
    } catch (err: any) {
      console.error('Failed to cancel retry:', err);
    } finally {
      setCancellingRetry(null);
    }
  };

  useEffect(() => {
    fetchAnalytics();
    fetchVendorHistory();
    fetchEmailAnalytics();
    fetchAbTestResults();
    fetchRetryStats();
  }, []);

  const handleExportCSV = async () => {
    try {
      setExporting(true);
      const token = await getToken();

      const blob = await apiFetch<Blob>('/api/v1/export/csv', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `claims_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      // Error toast handled by api utility
    } finally {
      setExporting(false);
    }
  };

  const handleExportExcel = async () => {
    try {
      setExporting(true);
      const token = await getToken();

      const blob = await apiFetch<Blob>('/api/v1/export/excel', {
        headers: { Authorization: `Bearer ${token}` }
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `claims_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      // Error toast handled by api utility
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-[var(--color-brand)] animate-spin mx-auto mb-4" />
          <p className="text-[var(--color-text-secondary)]">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--color-bg-base)] p-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-[var(--color-needs-human-tint)] border border-[var(--color-needs-human-tint-bd)] rounded-xl p-8">
            <XCircle className="w-12 h-12 text-[var(--color-brand)] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-[var(--color-needs-human-text)] mb-2">Failed to Load Analytics</h2>
            <p className="text-[var(--color-needs-human-text)] mb-4">{error || 'Unknown error'}</p>
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-[var(--color-brand)] text-[var(--color-on-brand)] rounded-lg hover:bg-[var(--color-brand-hover)]"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--color-bg-base)] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/')}
              className="p-2 hover:bg-[var(--color-bg-hover)] rounded-lg transition"
            >
              <ArrowLeft className="w-5 h-5 text-[var(--color-text-secondary)]" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-[var(--color-text-primary)]">Analytics Dashboard</h1>
              <p className="text-[var(--color-text-secondary)]">Business Intelligence & Performance Metrics</p>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={fetchAnalytics}
              className="px-4 py-2 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-lg text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-hover)] flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
            <button
              onClick={handleExportCSV}
              disabled={exporting}
              className="px-4 py-2 bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] rounded-lg hover:bg-[var(--color-bg-hover)] flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'CSV'}
            </button>
            <button
              onClick={handleExportExcel}
              disabled={exporting}
              className="px-4 py-2 bg-[var(--color-verified)] text-white rounded-lg hover:bg-[var(--color-verified)]/90 flex items-center gap-2 disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {exporting ? 'Exporting...' : 'Excel'}
            </button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[var(--color-needs-human-tint)] rounded-lg flex items-center justify-center">
                <FileText className="w-5 h-5 text-[var(--color-brand)]" />
              </div>
              <span className="text-sm text-[var(--color-text-muted)]">Total Claims</span>
            </div>
            <p className="text-3xl font-bold text-[var(--color-text-primary)]">{data.summary.total_claims}</p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              +{data.summary.recent_claims_7d} this week
            </p>
          </div>

          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-transparent rounded-lg flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-[var(--color-verified)]" />
              </div>
              <span className="text-sm text-[var(--color-text-muted)]">Total Value</span>
            </div>
            <p className="text-3xl font-bold text-[var(--color-text-primary)]"><FinancialValue value={data.summary.total_value} /></p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              Avg: <FinancialValue value={data.summary.average_value} />
            </p>
          </div>

          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-transparent rounded-lg flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-[var(--color-verified)]" />
              </div>
              <span className="text-sm text-[var(--color-text-muted)]">Completed</span>
            </div>
            <p className="text-3xl font-bold text-[var(--color-text-primary)]">{data.summary.completed_count}</p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              {data.summary.pending_count} pending
            </p>
          </div>

          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-[var(--color-needs-human-tint)] rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-[var(--color-brand)]" />
              </div>
              <span className="text-sm text-[var(--color-text-muted)]">Causation Pass Rate</span>
            </div>
            <p className="text-3xl font-bold text-[var(--color-text-primary)]">{data.causation_results.success_rate}%</p>
            <p className="text-xs text-[var(--color-text-muted)] mt-1">
              {data.causation_results.passed} passed / {data.causation_results.failed} failed causation tests
            </p>
          </div>
        </div>

        {/* Charts Row 1 */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Monthly Trends */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-[var(--color-brand)]" />
              Monthly Trends
            </h3>
            {data.monthly_trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={data.monthly_trends}>
                  <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                  <XAxis
                    dataKey="month"
                    tick={CHART_AXIS_STYLE}
                    tickFormatter={(value) => {
                      const [year, month] = value.split('-');
                      return `${month}/${year.slice(2)}`;
                    }}
                  />
                  <YAxis yAxisId="left" tick={CHART_AXIS_STYLE} />
                  <YAxis yAxisId="right" orientation="right" tick={CHART_AXIS_STYLE} />
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      name === 'value' ? formatCurrency(value) : value,
                      name === 'value' ? 'Total Value' : 'Claims'
                    ]}
                    contentStyle={CHART_TOOLTIP_STYLE}
                    labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                  />
                  <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="claims"
                    stroke={CHART_PALETTE[0]}
                    strokeWidth={2}
                    name="Claims"
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="value"
                    stroke={CHART_DONE_COLOR}
                    strokeWidth={2}
                    name="Value"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[var(--color-text-muted)]">
                No trend data available
              </div>
            )}
          </div>

          {/* Event Type Distribution */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <PieChartIcon className="w-5 h-5 text-[var(--color-brand)]" />
              Claims by Event Type
            </h3>
            {data.event_type_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={data.event_type_breakdown}
                    dataKey="count"
                    nameKey="event_type"
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    label={(props: any) =>
                      `${props.event_type} (${((props.percent ?? 0) * 100).toFixed(0)}%)`
                    }
                    labelLine={false}
                  >
                    {data.event_type_breakdown.map((_, index) => (
                      <Cell key={index} fill={EVENT_COLORS[index % EVENT_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    formatter={(value: number) => [value, 'Claims']}
                    contentStyle={CHART_TOOLTIP_STYLE}
                  />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[var(--color-text-muted)]">
                No event type data
              </div>
            )}
          </div>
        </div>

        {/* Charts Row 2 */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Status Breakdown */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[var(--color-brand)]" />
              Claims by Status
            </h3>
            {data.status_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.status_breakdown} layout="vertical">
                  <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                  <XAxis type="number" tick={CHART_AXIS_STYLE} />
                  <YAxis
                    type="category"
                    dataKey="status"
                    tick={CHART_AXIS_STYLE}
                    width={80}
                  />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL_STYLE} />
                  <Bar dataKey="count" name="Claims">
                    {data.status_breakdown.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={getStatusColor(entry.status)}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[var(--color-text-muted)]">
                No status data
              </div>
            )}
          </div>

          {/* Business Type */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[var(--color-brand)]" />
              Claims by Business Type
            </h3>
            {data.business_type_breakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={data.business_type_breakdown.slice(0, 6)}>
                  <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                  <XAxis
                    dataKey="business_type"
                    tick={CHART_AXIS_STYLE}
                    interval={0}
                    angle={-20}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis tick={CHART_AXIS_STYLE} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL_STYLE} />
                  <Bar dataKey="count" name="Claims" fill={CHART_PALETTE[0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[var(--color-text-muted)]">
                No business type data
              </div>
            )}
          </div>
        </div>

        {/* Top Claims Table */}
        <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-[var(--color-verified)]" />
            Top Claims by Value
          </h3>
          {data.top_claims.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[var(--color-border-faint)]">
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">Client</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">Event Type</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">Status</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-[var(--color-text-muted)]">Claim Value</th>
                  </tr>
                </thead>
                <tbody>
                  {data.top_claims.map((claim, index) => (
                    <tr
                      key={claim.id}
                      className="border-b border-[var(--color-border-faint)] hover:bg-[var(--color-bg-hover)] cursor-pointer"
                      onClick={() => navigate(`/report/${claim.id}`)}
                    >
                      <td className="py-3 px-4">
                        <span className="font-medium text-[var(--color-text-primary)]">{claim.client_name}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-[var(--color-text-secondary)]">{claim.event_type}</span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className="px-2 py-1 rounded-full text-xs font-medium"
                          style={{
                            backgroundColor: `${getStatusColor(claim.status)}20`,
                            color: getStatusColor(claim.status)
                          }}
                        >
                          {claim.status}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <FinancialValue value={claim.value} className="font-semibold text-[var(--color-verified)]" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-8 text-[var(--color-text-muted)]">
              No claims data available
            </div>
          )}
        </div>

        {/* Event Value Breakdown */}
        <div className="mt-6 bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
          <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
            <Calendar className="w-5 h-5 text-[var(--color-brand)]" />
            Total Value by Event Type
          </h3>
          {data.event_type_breakdown.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={data.event_type_breakdown}>
                <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                <XAxis dataKey="event_type" tick={CHART_AXIS_STYLE} />
                <YAxis tick={CHART_AXIS_STYLE} tickFormatter={(value) => `$${(value/1000).toFixed(0)}K`} />
                <Tooltip
                  formatter={(value: number) => [formatCurrency(value), 'Total Value']}
                  contentStyle={CHART_TOOLTIP_STYLE}
                  labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                />
                <Bar dataKey="total_value" name="Total Value">
                  {data.event_type_breakdown.map((_, index) => (
                    <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-[var(--color-text-muted)]">
              No value data available
            </div>
          )}
        </div>

        {/* NEW ANALYTICS SECTION */}
        <div className="mt-8 mb-4">
          <h2 className="text-xl font-bold text-[var(--color-text-primary)] flex items-center gap-2">
            <Activity className="w-6 h-6 text-[var(--color-brand)]" />
            Advanced Analytics
          </h2>
          <p className="text-[var(--color-text-muted)] text-sm mt-1">Deep insights into claim processing and data quality</p>
        </div>

        {/* Row 1: Health Score + Avg Completion Time + Calculation Mode Split */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
          {/* Health Score Distribution */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[var(--color-verified)]" />
              Claim Health Distribution
            </h3>
            {data.health_score_distribution ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Ready', value: data.health_score_distribution.green, color: HEALTH_COLORS.green },
                        { name: 'Needs Review', value: data.health_score_distribution.yellow, color: HEALTH_COLORS.yellow },
                        { name: 'Issues', value: data.health_score_distribution.red, color: HEALTH_COLORS.red }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {[HEALTH_COLORS.green, HEALTH_COLORS.yellow, HEALTH_COLORS.red].map((color, index) => (
                        <Cell key={index} fill={color} />
                      ))}
                    </Pie>
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: HEALTH_COLORS.green }}></span>
                    Ready ({data.health_score_distribution.green})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: HEALTH_COLORS.yellow }}></span>
                    Review ({data.health_score_distribution.yellow})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: HEALTH_COLORS.red }}></span>
                    Issues ({data.health_score_distribution.red})
                  </span>
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-[var(--color-text-muted)]">
                No health data available
              </div>
            )}
          </div>

          {/* Average Time to Completion */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-[var(--color-brand)]" />
              Processing Efficiency
            </h3>
            <div className="flex flex-col items-center justify-center h-[200px]">
              <div className="text-5xl font-bold text-[var(--color-brand)]">
                {data.summary.avg_completion_days || 0}
              </div>
              <div className="text-[var(--color-text-muted)] mt-2">Average Days to Complete</div>
              <div className="mt-4 grid grid-cols-2 gap-4 w-full text-center">
                <div className="bg-[var(--color-bg-elevated)] rounded-lg p-3">
                  <div className="text-lg font-bold text-[var(--color-verified)]">{data.summary.completed_count}</div>
                  <div className="text-xs text-[var(--color-text-muted)]">Completed</div>
                </div>
                <div className="bg-[var(--color-bg-elevated)] rounded-lg p-3">
                  <div className="text-lg font-bold text-[var(--color-text-secondary)]">{data.summary.pending_count}</div>
                  <div className="text-xs text-[var(--color-text-muted)]">In Progress</div>
                </div>
              </div>
            </div>
          </div>

          {/* Calculation Mode Split */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <Target className="w-5 h-5 text-[var(--color-brand)]" />
              Historical vs Future Closure
            </h3>
            {data.calculation_mode_split ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={[
                        { name: 'Historical BI', value: data.calculation_mode_split.historical, color: MODE_COLORS.historical },
                        { name: 'Future Closure', value: data.calculation_mode_split.future_closure, color: MODE_COLORS.future_closure }
                      ]}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      <Cell fill={MODE_COLORS.historical} />
                      <Cell fill={MODE_COLORS.future_closure} />
                    </Pie>
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex justify-center gap-4 mt-2 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: MODE_COLORS.historical }}></span>
                    Historical ({data.calculation_mode_split.historical})
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 rounded-full" style={{ backgroundColor: MODE_COLORS.future_closure }}></span>
                    Future ({data.calculation_mode_split.future_closure})
                  </span>
                </div>
              </>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-[var(--color-text-muted)]">
                No mode data available
              </div>
            )}
          </div>
        </div>

        {/* Row 2: BLS vs Actual + Top Clients */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* BLS vs Actual Data Usage */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <Database className="w-5 h-5 text-[var(--color-brand)]" />
              Data Source Usage
            </h3>
            {data.data_source_usage ? (
              <>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart
                    data={[
                      { name: 'Verified Actual', count: data.data_source_usage.actual, fill: SOURCE_COLORS.actual },
                      { name: 'BLS Averages', count: data.data_source_usage.bls_average, fill: SOURCE_COLORS.bls_average },
                      { name: 'Mixed Sources', count: data.data_source_usage.mixed, fill: SOURCE_COLORS.mixed }
                    ]}
                    layout="vertical"
                  >
                    <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                    <XAxis type="number" tick={CHART_AXIS_STYLE} />
                    <YAxis dataKey="name" type="category" tick={CHART_AXIS_STYLE} width={100} />
                    <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL_STYLE} />
                    <Bar dataKey="count" name="Claims">
                      <Cell fill={SOURCE_COLORS.actual} />
                      <Cell fill={SOURCE_COLORS.bls_average} />
                      <Cell fill={SOURCE_COLORS.mixed} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
                <div className="mt-2 text-xs text-[var(--color-text-muted)] text-center">
                  Claims using verified data have higher defensibility
                </div>
              </>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-[var(--color-text-muted)]">
                No source data available
              </div>
            )}
          </div>

          {/* Top Clients/Law Firms */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-[var(--color-brand)]" />
              Top Clients by Volume
            </h3>
            {data.top_clients && data.top_clients.length > 0 ? (
              <div className="space-y-3 max-h-[280px] overflow-y-auto">
                {data.top_clients.map((client, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-[var(--color-bg-elevated)] rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] rounded-full flex items-center justify-center text-xs font-bold">
                        {index + 1}
                      </span>
                      <span className="font-medium text-[var(--color-text-primary)] truncate max-w-[200px]">{client.client_name}</span>
                    </div>
                    <span className="bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] px-3 py-1 rounded-full text-sm font-semibold">
                      {client.count} claims
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[var(--color-text-muted)]">
                No client data available
              </div>
            )}
          </div>
        </div>

        {/* Row 3: Claim Value Distribution + Monte Carlo Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
          {/* Claim Value Distribution */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-[var(--color-verified)]" />
              Claim Value Distribution
            </h3>
            {data.claim_value_distribution && data.claim_value_distribution.length > 0 ? (
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={data.claim_value_distribution}>
                  <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                  <XAxis dataKey="bucket" tick={CHART_AXIS_STYLE} angle={-45} textAnchor="end" height={80} />
                  <YAxis tick={CHART_AXIS_STYLE} />
                  <Tooltip contentStyle={CHART_TOOLTIP_STYLE} labelStyle={CHART_TOOLTIP_LABEL_STYLE} />
                  <Bar dataKey="count" name="Claims" fill={CHART_DONE_COLOR} radius={[4, 4, 0, 0]}>
                    {data.claim_value_distribution.map((_, index) => (
                      <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[var(--color-text-muted)]">
                No distribution data available
              </div>
            )}
          </div>

          {/* Monte Carlo Aggregate Stats */}
          <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
            <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
              <Zap className="w-5 h-5 text-[var(--color-brand)]" />
              Monte Carlo Analysis Overview
            </h3>
            {data.monte_carlo_aggregate ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-[var(--color-needs-human-tint)] rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-[var(--color-brand)]">
                      {data.monte_carlo_aggregate.claims_with_mc}
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] mt-1">Claims with MC Analysis</div>
                  </div>
                  <div className="bg-[var(--color-bg-elevated)] rounded-xl p-4 text-center">
                    <div className="text-3xl font-bold text-[var(--color-text-primary)]">
                      {data.monte_carlo_aggregate.avg_ci_width_pct}%
                    </div>
                    <div className="text-xs text-[var(--color-text-muted)] mt-1">Avg CI Width (% of P50)</div>
                  </div>
                </div>

                <div className="bg-[var(--color-bg-elevated)] rounded-xl p-4">
                  <div className="text-sm text-[var(--color-text-muted)] mb-2">Average P50 (Median Estimate)</div>
                  <div className="text-2xl font-bold text-[var(--color-verified)]">
                    <FinancialValue value={data.monte_carlo_aggregate.avg_p50} />
                  </div>
                </div>

                <div className="bg-[var(--color-bg-elevated)] rounded-xl p-4">
                  <div className="text-sm text-[var(--color-text-muted)] mb-2">Aggregate 95% Confidence Range</div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-xs text-[var(--color-text-dim)]">Low (2.5%)</div>
                      <div className="text-lg font-semibold text-[var(--color-text-primary)]">
                        <FinancialValue value={data.monte_carlo_aggregate.total_ci_low} />
                      </div>
                    </div>
                    <div className="text-[var(--color-text-dim)]">—</div>
                    <div className="text-right">
                      <div className="text-xs text-[var(--color-text-dim)]">High (97.5%)</div>
                      <div className="text-lg font-semibold text-[var(--color-text-primary)]">
                        <FinancialValue value={data.monte_carlo_aggregate.total_ci_high} />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="text-xs text-[var(--color-text-muted)] text-center">
                  Monte Carlo simulations provide statistical confidence bounds
                </div>
              </div>
            ) : (
              <div className="h-[280px] flex items-center justify-center text-[var(--color-text-muted)]">
                No Monte Carlo data available
              </div>
            )}
          </div>
        </div>

        {/* Extraction Pipeline Analytics Section */}
        <div className="mt-8">
          <ExtractionAnalytics days={30} />
        </div>

        {/* Vendor Classification Cost Savings Over Time */}
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-[var(--color-text-primary)] flex items-center gap-2">
              <Database className="w-6 h-6 text-[var(--color-verified)]" />
              Vendor Classification Intelligence
            </h2>
            <p className="text-[var(--color-text-muted)] text-sm mt-1">Historical performance of multi-tier vendor classification system</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Classification Tier Trend Chart */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[var(--color-brand)]" />
                Classification Tier Distribution (6 Months)
              </h3>
              {vendorHistoryLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-[var(--color-text-dim)]" />
                </div>
              ) : vendorHistory?.monthly_data?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={vendorHistory.monthly_data} barCategoryGap="20%">
                      <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                      <XAxis
                        dataKey="month"
                        tick={CHART_AXIS_STYLE}
                        tickFormatter={(val) => {
                          const [year, month] = val.split('-');
                          return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short' });
                        }}
                      />
                      <YAxis tickFormatter={(val) => `${val}%`} domain={[0, 100]} tick={CHART_AXIS_STYLE} />
                      <Tooltip
                        formatter={(value: number, name: string) => {
                          const labels: Record<string, string> = {
                            'tier_1_rate': 'Tier 1 (Local DB)',
                            'tier_2_rate': 'Tier 2 (Local ML)',
                            'tier_3_rate': 'Tier 3 (AI API)'
                          };
                          return [`${value}%`, labels[name] || name];
                        }}
                        contentStyle={CHART_TOOLTIP_STYLE}
                        labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                      />
                      <Legend
                        formatter={(value) => {
                          const labels: Record<string, string> = {
                            'tier_1_rate': 'Tier 1 - Local DB (Free)',
                            'tier_2_rate': 'Tier 2 - ML (Free)',
                            'tier_3_rate': 'Tier 3 - AI API (Paid)'
                          };
                          return labels[value] || value;
                        }}
                        wrapperStyle={CHART_LEGEND_STYLE}
                      />
                      <Bar dataKey="tier_1_rate" stackId="a" fill={CHART_DONE_COLOR} name="tier_1_rate" />
                      <Bar dataKey="tier_2_rate" stackId="a" fill={CHART_PALETTE[0]} name="tier_2_rate" />
                      <Bar dataKey="tier_3_rate" stackId="a" fill={CHART_PALETTE[1]} name="tier_3_rate" />
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-6 mt-3 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: CHART_DONE_COLOR }}></span>
                      Tier 1 - Free (Local DB)
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: CHART_PALETTE[0] }}></span>
                      Tier 2 - Free (ML)
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: CHART_PALETTE[1] }}></span>
                      Tier 3 - Paid (AI API)
                    </span>
                  </div>
                </>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-[var(--color-text-muted)]">
                  No classification history available
                </div>
              )}
            </div>

            {/* Cost Savings Trend */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-[var(--color-verified)]" />
                Cumulative Cost Savings
              </h3>
              {vendorHistoryLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-[var(--color-text-dim)]" />
                </div>
              ) : vendorHistory?.monthly_data?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={vendorHistory.monthly_data}>
                      <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                      <XAxis
                        dataKey="month"
                        tick={CHART_AXIS_STYLE}
                        tickFormatter={(val) => {
                          const [year, month] = val.split('-');
                          return new Date(parseInt(year), parseInt(month) - 1).toLocaleDateString('en-US', { month: 'short' });
                        }}
                      />
                      <YAxis tickFormatter={(val) => `$${val}`} tick={CHART_AXIS_STYLE} />
                      <Tooltip
                        formatter={(value: number) => [`$${value.toFixed(2)}`, 'Cumulative Savings']}
                        contentStyle={CHART_TOOLTIP_STYLE}
                        labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                      />
                      <Line
                        type="monotone"
                        dataKey="cumulative_savings"
                        stroke={CHART_DONE_COLOR}
                        strokeWidth={3}
                        dot={{ fill: CHART_DONE_COLOR, strokeWidth: 2 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 mt-4">
                    <div className="bg-[var(--color-bg-elevated)] rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-[var(--color-verified)]">
                        ${vendorHistory.summary?.total_cumulative_savings?.toFixed(2) || '0.00'}
                      </div>
                      <div className="text-xs text-[var(--color-text-muted)]">Total Saved</div>
                    </div>
                    <div className="bg-[var(--color-bg-elevated)] rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-[var(--color-brand)]">
                        {vendorHistory.summary?.avg_free_rate?.toFixed(0) || 0}%
                      </div>
                      <div className="text-xs text-[var(--color-text-muted)]">Avg Free Rate</div>
                    </div>
                    <div className="bg-[var(--color-bg-elevated)] rounded-lg p-3 text-center">
                      <div className="text-2xl font-bold text-[var(--color-text-primary)]">
                        {(vendorHistory.summary?.database_vendors || 0).toLocaleString()}
                      </div>
                      <div className="text-xs text-[var(--color-text-muted)]">Vendors in DB</div>
                    </div>
                  </div>

                  <div className="mt-4 p-3 bg-[var(--color-bg-elevated)] rounded-lg text-center">
                    <p className="text-sm text-[var(--color-text-secondary)]">
                      As the local vendor database grows, more transactions are classified for <strong>free</strong> via Tier 1
                    </p>
                  </div>
                </>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-[var(--color-text-muted)]">
                  No savings data available
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Email Engagement Analytics Section */}
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-[var(--color-text-primary)] flex items-center gap-2">
              <Mail className="w-6 h-6 text-[var(--color-brand)]" />
              Client Email Engagement
            </h2>
            <p className="text-[var(--color-text-muted)] text-sm mt-1">Track open rates, click rates, and client response to evidence requests</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Key Metrics Cards */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <Activity className="w-5 h-5 text-[var(--color-brand)]" />
                Email Performance
              </h3>
              {emailAnalyticsLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-[var(--color-text-dim)]" />
                </div>
              ) : emailAnalytics?.summary ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-[var(--color-bg-elevated)] rounded-lg">
                    <div className="flex items-center gap-2">
                      <Mail className="w-5 h-5 text-[var(--color-brand)]" />
                      <span className="text-sm text-[var(--color-text-muted)]">Total Sent</span>
                    </div>
                    <span className="text-2xl font-bold text-[var(--color-brand)]">{emailAnalytics.summary.total_sent || 0}</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[var(--color-bg-elevated)] rounded-lg">
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-[var(--color-brand)]" />
                      <span className="text-sm text-[var(--color-text-muted)]">Open Rate</span>
                    </div>
                    <span className="text-2xl font-bold text-[var(--color-brand)]">{emailAnalytics.summary.open_rate_percent || 0}%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[var(--color-bg-elevated)] rounded-lg">
                    <div className="flex items-center gap-2">
                      <MousePointer className="w-5 h-5 text-[var(--color-verified)]" />
                      <span className="text-sm text-[var(--color-text-muted)]">Click Rate</span>
                    </div>
                    <span className="text-2xl font-bold text-[var(--color-verified)]">{emailAnalytics.summary.click_rate_percent || 0}%</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-[var(--color-bg-elevated)] rounded-lg">
                    <div className="flex items-center gap-2">
                      <Target className="w-5 h-5 text-[var(--color-text-secondary)]" />
                      <span className="text-sm text-[var(--color-text-muted)]">Click-to-Open</span>
                    </div>
                    <span className="text-2xl font-bold text-[var(--color-text-secondary)]">{emailAnalytics.summary.click_to_open_percent || 0}%</span>
                  </div>
                </div>
              ) : (
                <div className="h-[200px] flex items-center justify-center text-[var(--color-text-muted)] text-sm">
                  No email data available yet
                </div>
              )}
            </div>

            {/* Engagement Trend Chart */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)] lg:col-span-2">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-[var(--color-brand)]" />
                Email Engagement Over Time
              </h3>
              {emailAnalyticsLoading ? (
                <div className="h-[250px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-[var(--color-text-dim)]" />
                </div>
              ) : emailAnalytics?.daily_trend?.length > 0 ? (
                <>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={emailAnalytics.daily_trend}>
                      <CartesianGrid stroke={CHART_GRID_STYLE.stroke} strokeDasharray={CHART_GRID_STYLE.strokeDasharray} />
                      <XAxis
                        dataKey="date"
                        tick={CHART_AXIS_STYLE}
                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis tick={CHART_AXIS_STYLE} />
                      <Tooltip
                        labelFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                        formatter={(value: number, name: string) => {
                          const labels: Record<string, string> = {
                            'sent': 'Emails Sent',
                            'opened': 'Opened',
                            'clicked': 'Clicked'
                          };
                          return [value, labels[name] || name];
                        }}
                        contentStyle={CHART_TOOLTIP_STYLE}
                        labelStyle={CHART_TOOLTIP_LABEL_STYLE}
                      />
                      <Legend wrapperStyle={CHART_LEGEND_STYLE} />
                      <Line type="monotone" dataKey="sent" stroke={CHART_PALETTE[2]} name="sent" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="opened" stroke={CHART_PALETTE[0]} name="opened" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="clicked" stroke={CHART_DONE_COLOR} name="clicked" strokeWidth={2} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                  <div className="flex justify-center gap-6 mt-2 text-xs">
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: CHART_PALETTE[2] }}></span>
                      Sent
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: CHART_PALETTE[0] }}></span>
                      Opened
                    </span>
                    <span className="flex items-center gap-1">
                      <span className="w-3 h-3 rounded" style={{ backgroundColor: CHART_DONE_COLOR }}></span>
                      Clicked
                    </span>
                  </div>
                </>
              ) : (
                <div className="h-[250px] flex flex-col items-center justify-center text-[var(--color-text-muted)]">
                  <Mail className="w-12 h-12 text-[var(--color-text-dim)] mb-3" />
                  <p className="text-sm">No email engagement data yet</p>
                  <p className="text-xs text-[var(--color-text-dim)] mt-1">Send evidence requests to start tracking</p>
                </div>
              )}
            </div>
          </div>

          {/* Recent Email Activity */}
          {emailAnalytics?.recent_emails?.length > 0 && (
            <div className="mt-6 bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[var(--color-text-muted)]" />
                Recent Email Activity
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="email-activity-table">
                  <thead>
                    <tr className="border-b border-[var(--color-border)]">
                      <th className="text-left py-3 px-4 font-medium text-[var(--color-text-muted)]">Recipient</th>
                      <th className="text-left py-3 px-4 font-medium text-[var(--color-text-muted)]">Type</th>
                      <th className="text-left py-3 px-4 font-medium text-[var(--color-text-muted)]">Sent</th>
                      <th className="text-center py-3 px-4 font-medium text-[var(--color-text-muted)]">Opened</th>
                      <th className="text-center py-3 px-4 font-medium text-[var(--color-text-muted)]">Clicked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailAnalytics.recent_emails.slice(0, 10).map((email: any, idx: number) => (
                      <tr key={idx} className="border-b border-[var(--color-border-faint)] hover:bg-[var(--color-bg-hover)]">
                        <td className="py-3 px-4">
                          <span className="font-medium text-[var(--color-text-primary)]">{email.to_email}</span>
                        </td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 text-xs rounded-full bg-[var(--color-needs-human-tint)] text-[var(--color-brand)]">
                            {email.email_type?.replace('_', ' ') || 'evidence request'}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-[var(--color-text-secondary)]">
                          {email.sent_at ? new Date(email.sent_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          }) : '-'}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {email.open_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-[var(--color-brand)]">
                              <Eye className="w-4 h-4" />
                              {email.open_count}x
                            </span>
                          ) : (
                            <span className="text-[var(--color-text-dim)]">—</span>
                          )}
                        </td>
                        <td className="py-3 px-4 text-center">
                          {email.click_count > 0 ? (
                            <span className="inline-flex items-center gap-1 text-[var(--color-verified)]">
                              <MousePointer className="w-4 h-4" />
                              {email.click_count}x
                            </span>
                          ) : (
                            <span className="text-[var(--color-text-dim)]">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* A/B Test Results Section */}
        <div className="mt-8">
          <div className="mb-4">
            <h2 className="text-xl font-bold text-[var(--color-text-primary)] flex items-center gap-2">
              <FlaskConical className="w-6 h-6 text-[var(--color-brand)]" />
              Email A/B Test Results
            </h2>
            <p className="text-[var(--color-text-muted)] text-sm mt-1">Compare subject line and CTA button performance to optimize engagement</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Subject Line Test */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[var(--color-brand)]" />
                Subject Line Test
              </h3>
              <p className="text-xs text-[var(--color-text-muted)] mb-4">Optimizing for: Open Rate</p>

              {abTestLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-[var(--color-text-dim)]" />
                </div>
              ) : abTestResults?.results?.subject_line?.variants?.length > 0 ? (
                <div className="space-y-3">
                  {/* Locked Winner Banner */}
                  {lockedWinners?.subject_line && (
                    <div className="flex items-center justify-between p-3 bg-transparent border border-[var(--color-verified)] rounded-lg">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-[var(--color-verified)]" />
                        <span className="text-sm text-[var(--color-verified)]">
                          Locked to variant <strong>{lockedWinners.subject_line.variant_id}</strong> ({lockedWinners.subject_line.variant_name})
                        </span>
                      </div>
                      <button
                        onClick={() => clearWinner('subject_line')}
                        disabled={applyingWinner === 'clear_subject_line'}
                        className="px-2 py-1 text-xs text-[var(--color-verified)] hover:bg-transparent rounded transition-colors flex items-center gap-1"
                        data-testid="clear-subject-winner-btn"
                      >
                        {applyingWinner === 'clear_subject_line' ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Unlock className="w-3 h-3" />
                        )}
                        Resume Testing
                      </button>
                    </div>
                  )}

                  {abTestResults.results.subject_line.variants.map((variant: any, idx: number) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${variant.is_winner ? 'border-[var(--color-verified)] bg-transparent' : 'border-[var(--color-border)] bg-[var(--color-bg-elevated)]'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            variant.id === 'A' ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)]' :
                            variant.id === 'B' ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]' :
                            'bg-[var(--color-bg-hover)] text-[var(--color-text-muted)]'
                          }`}>
                            {variant.id}
                          </span>
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">{variant.name}</span>
                          {variant.is_winner && (
                            <Trophy className="w-4 h-4 text-[var(--color-verified)]" />
                          )}
                          {lockedWinners?.subject_line?.variant_id === variant.id && (
                            <Lock className="w-3 h-3 text-[var(--color-verified)]" />
                          )}
                        </div>
                        {/* Apply Winner Button - show only if has enough data and not already locked to this */}
                        {variant.sent >= 10 && !lockedWinners?.subject_line && (
                          <button
                            onClick={() => applyWinner('subject_line', variant.id)}
                            disabled={applyingWinner === `subject_line_${variant.id}`}
                            className="px-2 py-1 text-xs bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] hover:bg-[var(--color-needs-human-tint)]/80 rounded transition-colors flex items-center gap-1"
                            data-testid={`apply-subject-${variant.id}-btn`}
                          >
                            {applyingWinner === `subject_line_${variant.id}` ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Lock className="w-3 h-3" />
                            )}
                            Apply
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-[var(--color-text-muted)]">Sent: <strong>{variant.sent}</strong></span>
                        <span className="text-[var(--color-brand)] flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {variant.open_rate_percent}%
                        </span>
                        <span className="text-[var(--color-verified)] flex items-center gap-1">
                          <MousePointer className="w-3 h-3" />
                          {variant.click_rate_percent}%
                        </span>
                      </div>
                      {/* Progress bar for open rate */}
                      <div className="mt-2 h-2 bg-[var(--color-bg-hover)] rounded-full overflow-hidden">
                        <div
                          className={`h-full ${variant.is_winner ? 'bg-[var(--color-verified)]' : 'bg-[var(--color-brand)]'}`}
                          style={{ width: `${Math.min(variant.open_rate_percent, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-[var(--color-text-muted)] text-center mt-2">
                    Total emails: {abTestResults.results.subject_line.total_emails}
                  </p>
                </div>
              ) : (
                <div className="h-[200px] flex flex-col items-center justify-center text-[var(--color-text-muted)]">
                  <FlaskConical className="w-12 h-12 text-[var(--color-text-dim)] mb-3" />
                  <p className="text-sm">No A/B test data yet</p>
                  <p className="text-xs text-[var(--color-text-dim)] mt-1">Tests run automatically on each email</p>
                </div>
              )}
            </div>

            {/* CTA Button Test */}
            <div className="bg-[var(--color-bg-surface)] rounded-xl p-6 shadow-sm border border-[var(--color-border)]">
              <h3 className="text-lg font-semibold text-[var(--color-text-primary)] mb-4 flex items-center gap-2">
                <MousePointer className="w-5 h-5 text-[var(--color-verified)]" />
                CTA Button Test
              </h3>
              <p className="text-xs text-[var(--color-text-muted)] mb-4">Optimizing for: Click Rate</p>

              {abTestLoading ? (
                <div className="h-[200px] flex items-center justify-center">
                  <RefreshCw className="w-8 h-8 animate-spin text-[var(--color-text-dim)]" />
                </div>
              ) : abTestResults?.results?.cta_button?.variants?.length > 0 ? (
                <div className="space-y-3">
                  {/* Locked Winner Banner */}
                  {lockedWinners?.cta_button && (
                    <div className="flex items-center justify-between p-3 bg-transparent border border-[var(--color-verified)] rounded-lg">
                      <div className="flex items-center gap-2">
                        <Lock className="w-4 h-4 text-[var(--color-verified)]" />
                        <span className="text-sm text-[var(--color-verified)]">
                          Locked to variant <strong>{lockedWinners.cta_button.variant_id}</strong> ({lockedWinners.cta_button.variant_name})
                        </span>
                      </div>
                      <button
                        onClick={() => clearWinner('cta_button')}
                        disabled={applyingWinner === 'clear_cta_button'}
                        className="px-2 py-1 text-xs text-[var(--color-verified)] hover:bg-transparent rounded transition-colors flex items-center gap-1"
                        data-testid="clear-cta-winner-btn"
                      >
                        {applyingWinner === 'clear_cta_button' ? (
                          <RefreshCw className="w-3 h-3 animate-spin" />
                        ) : (
                          <Unlock className="w-3 h-3" />
                        )}
                        Resume Testing
                      </button>
                    </div>
                  )}

                  {abTestResults.results.cta_button.variants.map((variant: any, idx: number) => (
                    <div
                      key={idx}
                      className={`p-4 rounded-lg border ${variant.is_winner ? 'border-[var(--color-verified)] bg-transparent' : 'border-[var(--color-border)] bg-[var(--color-bg-elevated)]'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            variant.id === 'A' ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)]' :
                            variant.id === 'B' ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]' :
                            'bg-[var(--color-bg-hover)] text-[var(--color-text-muted)]'
                          }`}>
                            {variant.id}
                          </span>
                          <span className="text-sm font-medium text-[var(--color-text-primary)]">{variant.name}</span>
                          {variant.is_winner && (
                            <Trophy className="w-4 h-4 text-[var(--color-verified)]" />
                          )}
                          {lockedWinners?.cta_button?.variant_id === variant.id && (
                            <Lock className="w-3 h-3 text-[var(--color-verified)]" />
                          )}
                        </div>
                        {/* Apply Winner Button */}
                        {variant.sent >= 10 && !lockedWinners?.cta_button && (
                          <button
                            onClick={() => applyWinner('cta_button', variant.id)}
                            disabled={applyingWinner === `cta_button_${variant.id}`}
                            className="px-2 py-1 text-xs bg-[var(--color-needs-human-tint)] text-[var(--color-brand)] hover:bg-[var(--color-needs-human-tint)]/80 rounded transition-colors flex items-center gap-1"
                            data-testid={`apply-cta-${variant.id}-btn`}
                          >
                            {applyingWinner === `cta_button_${variant.id}` ? (
                              <RefreshCw className="w-3 h-3 animate-spin" />
                            ) : (
                              <Lock className="w-3 h-3" />
                            )}
                            Apply
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <span className="text-[var(--color-text-muted)]">Sent: <strong>{variant.sent}</strong></span>
                        <span className="text-[var(--color-brand)] flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {variant.open_rate_percent}%
                        </span>
                        <span className="text-[var(--color-verified)] flex items-center gap-1">
                          <MousePointer className="w-3 h-3" />
                          {variant.click_rate_percent}%
                        </span>
                      </div>
                      {/* Progress bar for click rate */}
                      <div className="mt-2 h-2 bg-[var(--color-bg-hover)] rounded-full overflow-hidden">
                        <div
                          className={`h-full ${variant.is_winner ? 'bg-[var(--color-verified)]' : 'bg-[var(--color-verified)]/70'}`}
                          style={{ width: `${Math.min(variant.click_rate_percent * 2, 100)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                  <p className="text-xs text-[var(--color-text-muted)] text-center mt-2">
                    Total emails: {abTestResults.results.cta_button.total_emails}
                  </p>
                </div>
              ) : (
                <div className="h-[200px] flex flex-col items-center justify-center text-[var(--color-text-muted)]">
                  <FlaskConical className="w-12 h-12 text-[var(--color-text-dim)] mb-3" />
                  <p className="text-sm">No A/B test data yet</p>
                  <p className="text-xs text-[var(--color-text-dim)] mt-1">Tests run automatically on each email</p>
                </div>
              )}
            </div>
          </div>

          <div className="mt-4 p-4 bg-[var(--color-needs-human-tint)] rounded-lg border border-[var(--color-needs-human-tint-bd)]">
            <p className="text-sm text-[var(--color-text-primary)]">
              <strong>How it works:</strong> Each evidence request email randomly receives one of three subject lines and one of three CTA button texts.
              Performance is tracked via open/click rates. The winner is determined when a variant shows statistically significant improvement (min 10 emails).
            </p>
          </div>
        </div>

        {/* Email Retry Queue Section */}
        <div className="bg-[var(--color-bg-surface)] rounded-xl shadow-sm border border-[var(--color-border)] p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[var(--color-needs-human-tint)] rounded-lg">
                <RotateCcw className="w-6 h-6 text-[var(--color-brand)]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">Email Retry Queue</h2>
                <p className="text-sm text-[var(--color-text-muted)]">Automatic retry for failed email deliveries</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={fetchRetryStats}
                disabled={retryLoading}
                className="p-2 text-[var(--color-text-dim)] hover:text-[var(--color-brand)] rounded-lg hover:bg-[var(--color-bg-hover)]"
                title="Refresh"
              >
                <RefreshCw className={`w-5 h-5 ${retryLoading ? 'animate-spin' : ''}`} />
              </button>
              {retryStats?.due_now > 0 && (
                <button
                  onClick={processRetries}
                  disabled={processingRetries}
                  className="flex items-center gap-2 px-3 py-2 bg-[var(--color-brand)] text-white rounded-lg hover:bg-[var(--color-brand-hover)] disabled:opacity-50 text-sm font-medium"
                >
                  <Play className="w-4 h-4" />
                  {processingRetries ? 'Processing...' : `Process ${retryStats.due_now} Now`}
                </button>
              )}
            </div>
          </div>

          {retryLoading ? (
            <div className="h-48 flex items-center justify-center">
              <RefreshCw className="w-8 h-8 text-[var(--color-text-dim)] animate-spin" />
            </div>
          ) : retryStats ? (
            <div className="space-y-6">
              {/* Stats Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-[var(--color-bg-elevated)] rounded-lg p-4 border border-[var(--color-border)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Clock className="w-4 h-4 text-[var(--color-brand)]" />
                    <span className="text-xs font-medium text-[var(--color-text-muted)]">Pending</span>
                  </div>
                  <p className="text-2xl font-bold text-[var(--color-text-primary)]">{retryStats.total_pending || 0}</p>
                </div>

                <div className="bg-[var(--color-bg-elevated)] rounded-lg p-4 border border-[var(--color-border)]">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-[var(--color-brand)]" />
                    <span className="text-xs font-medium text-[var(--color-text-muted)]">Due Now</span>
                  </div>
                  <p className="text-2xl font-bold text-[var(--color-brand)]">{retryStats.due_now || 0}</p>
                </div>

                <div className="bg-[var(--color-bg-elevated)] rounded-lg p-4 border border-[var(--color-border)]">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle2 className="w-4 h-4 text-[var(--color-verified)]" />
                    <span className="text-xs font-medium text-[var(--color-text-muted)]">Delivered</span>
                  </div>
                  <p className="text-2xl font-bold text-[var(--color-verified)]">{retryStats.total_delivered || 0}</p>
                </div>

                <div className="bg-[var(--color-bg-elevated)] rounded-lg p-4 border border-[var(--color-border)]">
                  <div className="flex items-center gap-2 mb-1">
                    <XOctagon className="w-4 h-4 text-[var(--color-text-muted)]" />
                    <span className="text-xs font-medium text-[var(--color-text-muted)]">Failed</span>
                  </div>
                  <p className="text-2xl font-bold text-[var(--color-text-muted)]">{retryStats.total_failed || 0}</p>
                </div>

                <div className="bg-[var(--color-bg-elevated)] rounded-lg p-4 border border-[var(--color-border)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Percent className="w-4 h-4 text-[var(--color-brand)]" />
                    <span className="text-xs font-medium text-[var(--color-text-muted)]">Success Rate (7d)</span>
                  </div>
                  <p className="text-2xl font-bold text-[var(--color-brand)]">{retryStats.success_rate_7d || 0}%</p>
                </div>
              </div>

              {/* Recent 24h Failures Alert */}
              {retryStats.recent_failures_24h > 0 && (
                <div className="p-3 bg-[var(--color-needs-human-tint)] border border-[var(--color-needs-human-tint-bd)] rounded-lg flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-[var(--color-brand)] flex-shrink-0" />
                  <p className="text-sm text-[var(--color-needs-human-text)]">
                    <strong>{retryStats.recent_failures_24h}</strong> email(s) permanently failed in the last 24 hours after exhausting all retries.
                  </p>
                </div>
              )}

              {/* Retry History Table */}
              <div>
                <h3 className="text-sm font-semibold text-[var(--color-text-primary)] mb-3">Recent Retry Jobs</h3>
                {retryHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-[var(--color-border)]">
                          <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)]">Email To</th>
                          <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)]">Type</th>
                          <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)]">Status</th>
                          <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)]">Retries</th>
                          <th className="text-left py-2 px-3 font-medium text-[var(--color-text-muted)]">Next Retry</th>
                          <th className="text-right py-2 px-3 font-medium text-[var(--color-text-muted)]">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {retryHistory.map((job: any) => (
                          <tr key={job._id} className="border-b border-[var(--color-border-faint)] hover:bg-[var(--color-bg-hover)]">
                            <td className="py-2 px-3">
                              <span className="text-[var(--color-text-primary)] truncate max-w-[150px] block">{job.to_email}</span>
                            </td>
                            <td className="py-2 px-3">
                              <span className="px-2 py-0.5 bg-[var(--color-bg-elevated)] rounded text-xs text-[var(--color-text-secondary)]">
                                {job.email_type?.replace('_', ' ')}
                              </span>
                            </td>
                            <td className="py-2 px-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                job.status === 'delivered' ? 'bg-transparent text-[var(--color-verified)]' :
                                job.status === 'failed' ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-needs-human-text)]' :
                                job.status === 'pending' ? 'bg-[var(--color-needs-human-tint)] text-[var(--color-brand)]' :
                                job.status === 'cancelled' ? 'bg-[var(--color-bg-elevated)] text-[var(--color-text-muted)]' :
                                'bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)]'
                              }`}>
                                {job.status}
                              </span>
                            </td>
                            <td className="py-2 px-3 text-[var(--color-text-secondary)]">
                              {job.retry_count || 0} / {job.max_retries || 4}
                            </td>
                            <td className="py-2 px-3 text-[var(--color-text-secondary)]">
                              {job.status === 'pending' && job.next_retry_at ? (
                                <span className="text-xs">
                                  {new Date(job.next_retry_at).toLocaleString()}
                                </span>
                              ) : job.status === 'delivered' && job.delivered_at ? (
                                <span className="text-[var(--color-verified)] text-xs">
                                  ✓ {new Date(job.delivered_at).toLocaleString()}
                                </span>
                              ) : (
                                <span className="text-[var(--color-text-dim)]">—</span>
                              )}
                            </td>
                            <td className="py-2 px-3 text-right">
                              {job.status === 'pending' && (
                                <button
                                  onClick={() => cancelRetry(job._id)}
                                  disabled={cancellingRetry === job._id}
                                  className="px-2 py-1 text-xs text-[var(--color-brand)] hover:bg-[var(--color-needs-human-tint)] rounded disabled:opacity-50"
                                >
                                  {cancellingRetry === job._id ? '...' : 'Cancel'}
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-[var(--color-text-muted)]">
                    <Mail className="w-10 h-10 mx-auto text-[var(--color-text-dim)] mb-2" />
                    <p className="text-sm">No retry jobs yet</p>
                    <p className="text-xs text-[var(--color-text-dim)] mt-1">Failed emails will automatically appear here</p>
                  </div>
                )}
              </div>

              {/* How it works info */}
              <div className="p-4 bg-[var(--color-needs-human-tint)] rounded-lg border border-[var(--color-needs-human-tint-bd)]">
                <p className="text-sm text-[var(--color-text-primary)]">
                  <strong>How it works:</strong> When an email fails to send, it's automatically queued for retry with exponential backoff (5min → 15min → 1hr → 4hr).
                  After 4 failed attempts, the email is marked as permanently failed. Retries are processed every 5 minutes by the background scheduler.
                </p>
              </div>
            </div>
          ) : (
            <div className="h-48 flex flex-col items-center justify-center text-[var(--color-text-muted)]">
              <RotateCcw className="w-12 h-12 text-[var(--color-text-dim)] mb-3" />
              <p className="text-sm">Unable to load retry stats</p>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
